import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Car, Plus, Link2, Clock, MapPin, AlertTriangle } from 'lucide-react';
import { useCompare } from '@/context/CompareContext';

const OTDQuoteRequest = () => {
  const navigate = useNavigate();
  const { getParsedCars } = useCompare();
  
  const [shortlistCars, setShortlistCars] = useState([]);
  const [selectedCars, setSelectedCars] = useState({});
  const [manualCars, setManualCars] = useState([
    { year: '', make: '', model: '', trim: '', zip: '', linkOrVin: '' }
  ]);
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      const cars = getParsedCars();
      setShortlistCars(cars);
      
      // Select all shortlist cars by default
      const initialSelected = {};
      cars.forEach((car, idx) => {
        initialSelected[idx] = true;
      });
      setSelectedCars(initialSelected);
      setInitialized(true);
    }
  }, [getParsedCars, initialized]);

  const handleBack = () => {
    navigate('/tools/compare-cars');
  };

  const toggleCarSelection = (idx) => {
    setSelectedCars(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
    setError('');
  };

  const updateManualCar = (idx, field, value) => {
    setManualCars(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
    setError('');
  };

  const addManualCar = () => {
    setManualCars(prev => [
      ...prev,
      { year: '', make: '', model: '', trim: '', zip: '', linkOrVin: '' }
    ]);
  };

  const isManualCarValid = (car) => {
    return car.year && car.make && car.model && car.zip && car.zip.length === 5;
  };

  const isManualCarPartiallyFilled = (car) => {
    return car.year || car.make || car.model || car.trim || car.zip || car.linkOrVin;
  };

  const handleCreateEmail = () => {
    // Get selected shortlist cars
    const selectedShortlistCars = shortlistCars
      .filter((_, idx) => selectedCars[idx])
      .map(car => ({
        ...car,
        type: 'shortlist'
      }));

    // Get valid manual cars
    const validManualCars = manualCars
      .filter(car => isManualCarValid(car))
      .map(car => ({
        displayText: `${car.year} ${car.make} ${car.model}${car.trim ? ` ${car.trim}` : ''}`,
        year: car.year,
        makeModel: `${car.make} ${car.model}${car.trim ? ` ${car.trim}` : ''}`,
        link: car.linkOrVin || null,
        zip: car.zip,
        type: 'manual'
      }));

    // Check for partially filled but invalid manual cars
    const partialManualCars = manualCars.filter(car => 
      isManualCarPartiallyFilled(car) && !isManualCarValid(car)
    );

    if (partialManualCars.length > 0) {
      setError('Please complete all required fields (Year, Make, Model, ZIP) for manually added cars.');
      return;
    }

    // Validation: at least one car
    const totalCars = selectedShortlistCars.length + validManualCars.length;
    if (totalCars === 0) {
      setError('Please select at least one car from your shortlist or add a car manually.');
      return;
    }

    // Collect all zip codes (from manual cars or use a default prompt)
    const allCars = [...selectedShortlistCars, ...validManualCars];
    
    // Get first available zip code for shortlist cars
    const defaultZip = validManualCars.length > 0 ? validManualCars[0].zip : '';

    // Navigate to email generator
    navigate('/tools/otd-email-generate', { 
      state: { 
        cars: allCars,
        zipCode: defaultZip
      }
    });
  };

  const selectedShortlistCount = Object.values(selectedCars).filter(v => v).length;
  const validManualCount = manualCars.filter(isManualCarValid).length;
  const totalSelected = selectedShortlistCount + validManualCount;

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="bg-white border-b" style={{ borderBottomColor: '#E5E7EB' }}>
        <div className="max-w-screen-sm mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
              style={{ color: '#0B1221' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <span className="text-sm font-semibold tracking-wide">
              <span style={{ color: '#001F3F' }}>VINdicated.</span>
              <span style={{ color: '#FF4136' }}>ai</span>
            </span>
          </div>
          
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: '#001F3F' }}>
            Choose cars for your quote request
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-sm mx-auto px-4 py-6 space-y-6">
        
        {/* Protect Your Time Info Note */}
        <div 
          className="rounded-xl p-4 flex items-start gap-3"
          style={{ 
            backgroundColor: '#FFFFFF', 
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#F0F9FF' }}
          >
            <Clock className="w-[18px] h-[18px]" style={{ color: '#001F3F' }} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#001F3F' }}>
              Protect your time.
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280', fontStyle: 'italic' }}>
              Protect your time the same way you protect your money.
            </p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: '#4B5563' }}>
              A written out-the-door quote lets you compare totals from home before you drive anywhere. If a dealer insists you come in "just to get the price," treat that as a red flag. The best dealers will put the full number in writing and make it easy to verify.
            </p>
          </div>
        </div>
        
        {/* Cast a Wider Net Info Note */}
        <div 
          className="rounded-xl p-4 flex items-start gap-3"
          style={{ 
            backgroundColor: '#FFFFFF', 
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#F0F9FF' }}
          >
            <MapPin className="w-[18px] h-[18px]" style={{ color: '#001F3F' }} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#001F3F' }}>
              Cast a wider net: use one good quote to your advantage.
            </p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: '#4B5563' }}>
              A written out-the-door quote from a farther dealer can be a helpful benchmark (a clean reference price). Either use it to ask a closer dealer to match it, or choose the dealer that is easiest to work with and make the drive.
            </p>
          </div>
        </div>
        
        {/* Caution Info Note */}
        <div 
          className="rounded-xl p-4 flex items-start gap-3"
          style={{ 
            backgroundColor: '#FFFFFF', 
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#F0F9FF' }}
          >
            <AlertTriangle className="w-[18px] h-[18px]" style={{ color: '#001F3F' }} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#001F3F' }}>
              If they won't give totals, that's a signal
            </p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: '#4B5563' }}>
              Many dealers will ask you to "come in" instead of sending an out the door quote. A serious buyer does due diligence. If a dealer won't share totals in writing, use caution and consider moving on.
            </p>
          </div>
        </div>
        
        {/* Section A: Cars from shortlist */}
        {shortlistCars.length > 0 && (
          <div className="rounded-xl p-5 shadow-sm" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#DBEAFE' }}
              >
                <Car className="w-[18px] h-[18px]" style={{ color: '#001F3F' }} strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-base font-semibold" style={{ color: '#001F3F' }}>
                  Cars from your shortlist
                </h2>
                <p className="text-xs" style={{ color: '#6B7280' }}>
                  Select which cars to include in your out-the-door quote email.
                </p>
              </div>
            </div>

            <div className="space-y-2 mt-4">
              {shortlistCars.map((car, idx) => (
                <button
                  key={idx}
                  onClick={() => toggleCarSelection(idx)}
                  className="w-full text-left rounded-lg p-3 transition-all"
                  style={{ 
                    backgroundColor: selectedCars[idx] ? '#F0F9FF' : '#F9FAFB',
                    border: selectedCars[idx] ? '2px solid #1D4ED8' : '1px solid #E5E7EB'
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ 
                        backgroundColor: selectedCars[idx] ? '#1D4ED8' : '#FFFFFF',
                        border: selectedCars[idx] ? 'none' : '2px solid #D1D5DB'
                      }}
                    >
                      {selectedCars[idx] && (
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#111827' }}>
                        {car.displayText}
                      </p>
                      <p className="text-xs mt-0.5 truncate" style={{ color: '#6B7280' }}>
                        {car.link ? car.link : 'From your compare shortlist'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Section B: Add car manually */}
        <div className="rounded-xl p-5 shadow-sm" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
          <h2 className="text-base font-semibold mb-1" style={{ color: '#001F3F' }}>
            Add a car manually
          </h2>
          <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
            Use this if you want quotes on a car that isn't in your shortlist.
          </p>

          <div className="space-y-4">
            {manualCars.map((car, idx) => (
              <div 
                key={idx} 
                className="rounded-lg p-4"
                style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
              >
                {idx > 0 && (
                  <p className="text-xs font-medium mb-3" style={{ color: '#6B7280' }}>
                    Additional car {idx + 1}
                  </p>
                )}
                
                {/* Row 1: Year, Make, Model, Trim */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                      Year*
                    </label>
                    <input
                      type="text"
                      value={car.year}
                      onChange={(e) => updateManualCar(idx, 'year', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="2024"
                      maxLength={4}
                      className="w-full px-2 py-2 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                      Make*
                    </label>
                    <input
                      type="text"
                      value={car.make}
                      onChange={(e) => updateManualCar(idx, 'make', e.target.value)}
                      placeholder="Toyota"
                      className="w-full px-2 py-2 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                      Model*
                    </label>
                    <input
                      type="text"
                      value={car.model}
                      onChange={(e) => updateManualCar(idx, 'model', e.target.value)}
                      placeholder="Camry"
                      className="w-full px-2 py-2 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                      Trim
                    </label>
                    <input
                      type="text"
                      value={car.trim}
                      onChange={(e) => updateManualCar(idx, 'trim', e.target.value)}
                      placeholder="SE"
                      className="w-full px-2 py-2 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                    />
                  </div>
                </div>

                {/* Row 2: ZIP */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                      ZIP*
                    </label>
                    <input
                      type="text"
                      value={car.zip}
                      onChange={(e) => updateManualCar(idx, 'zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="90210"
                      maxLength={5}
                      className="w-full px-2 py-2 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                    />
                  </div>
                  <div className="col-span-3"></div>
                </div>

                {/* Row 3: Link or VIN */}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                    <div className="flex items-center gap-1">
                      <Link2 className="w-3 h-3" />
                      <span>Listing link or VIN</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={car.linkOrVin}
                    onChange={(e) => updateManualCar(idx, 'linkOrVin', e.target.value)}
                    placeholder="Paste the listing link or VIN (optional)"
                    className="w-full px-3 py-2 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
                  />
                </div>
              </div>
            ))}

            {/* Add another car button */}
            <button
              onClick={addManualCar}
              className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
              style={{ color: '#1D4ED8' }}
            >
              <Plus className="w-4 h-4" />
              <span>Add another car</span>
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-lg px-4 py-3" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
            <p className="text-sm" style={{ color: '#991B1B' }}>{error}</p>
          </div>
        )}

        {/* V2 Badge */}
        <div className="flex justify-center">
          <span 
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: '#F0F9FF', color: '#1D4ED8', border: '1px solid #DBEAFE' }}
          >
            ⭐ V2: Request quotes in-app with VINdicated - keep your email private.
          </span>
        </div>

        {/* Create button */}
        <button
          onClick={handleCreateEmail}
          className="w-full py-4 px-6 rounded-xl font-bold text-lg transition-all shadow-md flex items-center justify-center gap-2 hover:opacity-90"
          style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
        >
          <span>Create my quote request email</span>
          <ArrowRight className="w-5 h-5" style={{ color: '#FF4136' }} strokeWidth={2} />
        </button>

        {/* Disclaimer */}
        <p className="text-xs text-center pt-4" style={{ color: '#9CA3AF' }}>
          Educational use only. Not financial, legal, or tax advice.
        </p>
      </div>
    </div>
  );
};

export default OTDQuoteRequest;
